import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { usePermission } from '../../hooks/usePermission';

const RESOURCES = [
  { key: 'detections', label: 'Detections', description: 'Security detections and alerts' },
  { key: 'cases', label: 'Cases', description: 'Investigation cases' },
  { key: 'indicators', label: 'Indicators', description: 'Threat indicators (IOCs)' },
  { key: 'entities', label: 'Entities', description: 'Hosts, users and other entities' },
  { key: 'agents', label: 'Agents', description: 'EDR / security product integrations' },
  { key: 'users', label: 'Users', description: 'User accounts and invitations' },
  { key: 'company', label: 'Company', description: 'Company and system settings' },
  { key: 'billing', label: 'Billing', description: 'Subscription and billing' },
  { key: 'reports', label: 'Reports', description: 'Generate and view reports' },
  { key: 'roles', label: 'Roles', description: 'Roles and permissions' }
];

const ACTIONS = [
  { key: 'create', label: 'Create' },
  { key: 'read', label: 'Read' },
  { key: 'update', label: 'Update' },
  { key: 'delete', label: 'Delete' },
  { key: 'manage', label: 'Manage (all)' }
];

const NAME_PATTERN = /^[a-z][a-z0-9_]{1,40}$/;

const toSnake = (value) => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .replace(/^[^a-z]+/, '')
  .slice(0, 41);

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const EMPTY_FORM = { name: '', displayName: '', description: '', permissions: {} };

const RoleManagement = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission('roles', 'create');
  const canUpdate = hasPermission('roles', 'update');
  const canDelete = hasPermission('roles', 'delete');

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // role being edited/viewed
  const [viewOnly, setViewOnly] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [nameTouched, setNameTouched] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.roles.getAll();
      setRoles(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load roles'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const permissionsToObject = (permissions) => {
    const obj = {};
    (permissions || []).forEach(perm => { obj[perm.resource] = [...(perm.actions || [])]; });
    return obj;
  };

  const openCreate = () => {
    setEditingRole(null);
    setViewOnly(false);
    setNameTouched(false);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openRole = (role, readOnly) => {
    setEditingRole(role);
    setViewOnly(readOnly);
    setNameTouched(true);
    setFormData({
      name: role.name || '',
      displayName: role.displayName || role.name || '',
      description: role.description || '',
      permissions: permissionsToObject(role.permissions)
    });
    setDialogOpen(true);
  };

  const handleDisplayNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      displayName: value,
      // Derive the identifier from the display name until the user edits it directly
      name: !editingRole && !nameTouched ? toSnake(value) : prev.name
    }));
  };

  const handlePermissionChange = (resource, action, checked) => {
    setFormData(prev => {
      const current = prev.permissions[resource] || [];
      const next = checked ? [...new Set([...current, action])] : current.filter(a => a !== action);
      return { ...prev, permissions: { ...prev.permissions, [resource]: next } };
    });
  };

  const nameError = formData.name && !NAME_PATTERN.test(formData.name)
    ? 'Lowercase letters, numbers and underscores only, starting with a letter (e.g. tier1_analyst)'
    : null;

  const handleSaveRole = async () => {
    const permissions = Object.entries(formData.permissions)
      .filter(([, actions]) => actions && actions.length > 0)
      .map(([resource, actions]) => ({ resource, actions }));

    if (!formData.displayName.trim() || !formData.description.trim()) {
      setError('Display name and description are required');
      return;
    }
    if (!editingRole && (nameError || !formData.name)) {
      setError(nameError || 'Role identifier is required');
      return;
    }

    const payload = {
      name: formData.name,
      displayName: formData.displayName.trim(),
      description: formData.description.trim(),
      permissions
    };

    try {
      setSaving(true);
      setError(null);
      if (editingRole) {
        await apiService.roles.update(editingRole._id, payload);
        setSuccess('Role updated successfully');
      } else {
        await apiService.roles.create(payload);
        setSuccess('Role created successfully');
      }
      setDialogOpen(false);
      fetchRoles();
    } catch (err) {
      setError(serverMessage(err, 'Failed to save role'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      setError(null);
      const { data } = await apiService.roles.delete(deleteTarget._id);
      setSuccess(data?.message || 'Role deleted successfully');
      setDeleteTarget(null);
      fetchRoles();
    } catch (err) {
      setError(serverMessage(err, 'Failed to delete role'));
    } finally {
      setSaving(false);
    }
  };

  const formatPermissions = (permissions) => (permissions || []).map(perm => (
    <Chip
      key={perm.resource}
      label={`${perm.resource}: ${(perm.actions || []).join(', ')}`}
      size="small"
      variant="outlined"
      sx={{ mr: 0.5, mb: 0.5 }}
    />
  ));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const readOnlyDialog = viewOnly || (editingRole && editingRole.isSystemRole);

  return (
    <Card>
      <CardHeader
        title="Role Management"
        subheader="Built-in roles are managed by the platform; create custom roles for finer-grained access"
        avatar={<SecurityIcon />}
        action={
          <Box display="flex" gap={1}>
            <Button startIcon={<RefreshIcon />} onClick={fetchRoles} disabled={saving}>Refresh</Button>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                Create Role
              </Button>
            )}
          </Box>
        }
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>System roles</strong> (Company Admin, Admin, Manager, Senior Analyst, Analyst, Viewer) are built
          into the platform and cannot be edited or deleted; open one to review its permissions. Custom roles can be
          edited at any time and deleted once no users are assigned to them.
        </Alert>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Users</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">No roles found</Typography>
                  </TableCell>
                </TableRow>
              )}
              {roles.map((role) => (
                <TableRow key={role._id || role.name}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <AdminIcon sx={{ mr: 1, color: role.isSystemRole ? 'primary.main' : 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2">{role.displayName || role.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{role.name}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{role.description}</TableCell>
                  <TableCell>
                    <Box sx={{ maxWidth: 320 }}>{formatPermissions(role.permissions)}</Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {role.isSystemRole
                        ? <Chip label="System" color="primary" size="small" />
                        : <Chip label="Custom" size="small" />}
                      {role.isDefault && <Chip label="Default" size="small" variant="outlined" />}
                    </Box>
                  </TableCell>
                  <TableCell align="center">{role.userCount ?? 0}</TableCell>
                  <TableCell align="right">
                    {role.isSystemRole ? (
                      <Tooltip title="View permissions (system roles are read-only)">
                        <IconButton size="small" onClick={() => openRole(role, true)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title={canUpdate ? 'Edit role' : 'View role'}>
                          <IconButton size="small" onClick={() => openRole(role, !canUpdate)}>
                            {canUpdate ? <EditIcon /> : <ViewIcon />}
                          </IconButton>
                        </Tooltip>
                        {canDelete && (
                          <Tooltip title={role.userCount > 0 ? `${role.userCount} user(s) still assigned` : 'Delete role'}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={role.userCount > 0}
                                onClick={() => setDeleteTarget(role)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Role dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {readOnlyDialog ? `Role: ${formData.displayName}` : editingRole ? 'Edit Role' : 'Create New Role'}
          </DialogTitle>
          <DialogContent>
            {editingRole?.isSystemRole && (
              <Alert severity="info" sx={{ mt: 1 }}>
                This is a system role. Its permissions are defined by the platform and cannot be changed; create a
                custom role if you need a different set of permissions.
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={formData.displayName}
                  onChange={(e) => handleDisplayNameChange(e.target.value)}
                  disabled={readOnlyDialog}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Identifier"
                  value={formData.name}
                  onChange={(e) => { setNameTouched(true); setFormData(prev => ({ ...prev, name: e.target.value })); }}
                  disabled={readOnlyDialog || Boolean(editingRole)}
                  error={Boolean(nameError)}
                  helperText={nameError || (editingRole ? 'Identifiers cannot be changed after creation' : 'lowercase_snake_case; used internally when assigning users')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={readOnlyDialog}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>Permissions</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  "Manage" grants every action on a resource. Leave a resource unchecked to deny access to it.
                </Typography>
                {RESOURCES.map(resource => (
                  <Box key={resource.key} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">{resource.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {resource.description}
                    </Typography>
                    <FormGroup row>
                      {ACTIONS.map(action => (
                        <FormControlLabel
                          key={action.key}
                          control={
                            <Checkbox
                              size="small"
                              checked={(formData.permissions[resource.key] || []).includes(action.key)}
                              onChange={(e) => handlePermissionChange(resource.key, action.key, e.target.checked)}
                              disabled={readOnlyDialog}
                            />
                          }
                          label={action.label}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{readOnlyDialog ? 'Close' : 'Cancel'}</Button>
            {!readOnlyDialog && (
              <Button onClick={handleSaveRole} variant="contained" disabled={saving}>
                {saving ? <CircularProgress size={20} /> : editingRole ? 'Update' : 'Create'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
          <DialogTitle>Delete Role</DialogTitle>
          <DialogContent>
            <Typography>
              Delete the role "{deleteTarget?.displayName || deleteTarget?.name}"? This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDeleteRole} color="error" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default RoleManagement;
